import { useEffect, useRef, useState } from 'react';
import { HookProvider, useHook } from '@hook-sdk/sdk';
import { config } from '../config';
import { useAppMeta, type AppMeta } from './useAppMeta';
import { useBundleLoad } from './useBundleLoad';
import { AppErrorScreen } from './AppErrorScreen';
import { AppLoadingSkeleton } from './AppLoadingSkeleton';
import { ShellErrorBoundary } from './ShellErrorBoundary';
import { updatePwaMeta, resetPwaMeta } from '../sw/pwaMeta';
import { analytics } from '../observability/posthog';

export function AppLoader({ slug }: { slug: string }) {
  const [retryNonce, setRetryNonce] = useState(0);
  const meta = useAppMeta(slug);

  useEffect(() => {
    if (!meta.data) return;
    updatePwaMeta({
      name: meta.data.name,
      logoUrl: meta.data.logo_url,
      themeColor: meta.data.primary_color,
    });
    return () => resetPwaMeta();
  }, [meta.data]);

  if (meta.loading) return <AppLoadingSkeleton />;

  if (meta.error) {
    return (
      <AppErrorScreen
        code={meta.error.code}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const data = meta.data!;
  const bundleUrl = `${config.r2Host}/${data.slug}/app.js`;

  return (
    <AppLoaderInner
      slug={slug}
      appId={data.app_id}
      bundleUrl={bundleUrl}
      meta={data}
      retryNonce={retryNonce}
      onRetry={() => setRetryNonce((n) => n + 1)}
    />
  );
}

function AppLoaderInner({
  slug,
  appId,
  bundleUrl,
  meta,
  retryNonce,
  onRetry,
}: {
  slug: string;
  appId: string;
  bundleUrl: string;
  meta: AppMeta;
  retryNonce: number;
  onRetry: () => void;
}) {
  const bundle = useBundleLoad(slug, bundleUrl, retryNonce, appId);

  // PostHog group analytics: cada app é um group_type='app' separado, mesma
  // PostHog project pra plataforma toda. Dispara no mount do AppLoaderInner
  // — uma vez por (appId, slug), independente do estado do bundle. Eventos
  // subsequentes deste tab são automaticamente atribuídos ao group via
  // PostHog $groups context. Ver ADR-019.
  //
  // O `app_opened` em si é emitido pelo <AppOpenedTracker> dentro do
  // HookProvider — precisamos do `authStatus` (vem via useHook()) pra
  // marcar o evento como anonymous vs authenticated, e o provider só
  // existe lá embaixo. `group()` fica aqui porque não depende de auth.
  useEffect(() => {
    analytics.group('app', appId, { name: meta.slug });
  }, [appId, meta.slug]);

  if (bundle.loading) return <AppLoadingSkeleton meta={meta} />;

  if (bundle.error) {
    return (
      <AppErrorScreen
        code={bundle.error.code}
        onRetry={onRetry}
        traceId={bundle.error.traceId}
      />
    );
  }

  const App = bundle.App!;
  // Assets live next to the bundle in R2: `<slug>/app.js` and `<slug>/assets/*`.
  // Passing the host in at mount time means one universal bundle works in any
  // environment — bundle never bakes in a host. See docs/04-bundle-pipeline.md
  // § "Assets estáticos do app".
  const assetsHost = `${config.r2Host}/${slug}/assets/`;
  // bundle_version proxy: AppMeta exposes sdk_version (the @hook-sdk/sdk semver
  // the bundle was built against). The bundle's actual content fingerprint is
  // the SRI hash (bundle.sri). Together they identify which build threw.
  const bundleVersion = meta.sdk_version ?? 'unknown';
  return (
    <ShellErrorBoundary
      onReset={onRetry}
      appId={appId}
      bundleVersion={bundleVersion}
      sri={bundle.sri}
    >
      <HookProvider config={{ appId, appSlug: slug, apiHost: config.apiHost, assetsHost }}>
        <AppOpenedTracker appId={appId} slug={slug} />
        <App />
      </HookProvider>
    </ShellErrorBoundary>
  );
}

/**
 * Emite `app_opened` uma vez por (appId, slug) com a property
 * `authenticated` populada — distingue visitante anônimo de user já
 * logado. Studio usa pra separar "novos visitantes" de "users abrindo
 * pra usar o app" no funil de drop-off.
 *
 * Espera authStatus sair de 'loading' antes de emitir — caso contrário,
 * o estado inicial 'loading' (auth/me ainda em flight) marcaria como
 * anônimo gente que tem sessão válida.
 *
 * Renderiza nada — pure side effect.
 */
function AppOpenedTracker({ appId, slug }: { appId: string; slug: string }) {
  const { authStatus } = useHook();
  const emittedRef = useRef(false);

  useEffect(() => {
    if (emittedRef.current) return;
    if (authStatus === 'loading') return;
    emittedRef.current = true;
    analytics.capture('app_opened', {
      app_id: appId,
      app_slug: slug,
      authenticated: authStatus === 'authenticated',
    });
  }, [appId, slug, authStatus]);

  return null;
}
